// Charts of a grid range (design 02 12.1): bar, line and pie, drawn as SVG.
// The sheet keeps each chart's settings (sheets.charts on the server, passed
// through as they are); this module turns settings plus the range's values
// into shapes. No DOM here, so it is tested on its own.

export type ChartType = "bar" | "line" | "pie";

export interface ChartSpec {
  id: string;
  type: ChartType;
  /** the data, "A1:C7" on the chart's sheet; the first row and column may be labels */
  range: string;
  title?: string;
  /** series are the columns (default) or the rows */
  by?: "cols" | "rows";
  /** where it sits over the grid, px from the sheet's top left */
  x: number;
  y: number;
  w: number;
  h: number;
}

export const CHART_TYPES: ChartType[] = ["bar", "line", "pie"];
export const CHART_MIN = { w: 200, h: 150 };

export function chartSpecValid(v: unknown): v is ChartSpec {
  if (typeof v !== "object" || v === null) return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c.id === "string" &&
    CHART_TYPES.includes(c.type as ChartType) &&
    typeof c.range === "string" &&
    /^\$?[A-Za-z]{1,3}\$?\d+:\$?[A-Za-z]{1,3}\$?\d+$/.test(c.range) &&
    (c.title === undefined || typeof c.title === "string") &&
    (c.by === undefined || c.by === "cols" || c.by === "rows") &&
    [c.x, c.y, c.w, c.h].every((n) => typeof n === "number" && Number.isFinite(n))
  );
}

/** The charts of a sheet's `charts` (unknown shapes from other versions are left out) */
export function readCharts(raw: unknown): ChartSpec[] {
  return Array.isArray(raw) ? raw.filter(chartSpecValid) : [];
}

export type CellValue = number | string | boolean | null;

export interface Series {
  name: string;
  values: (number | null)[];
}

export interface ChartData {
  labels: string[];
  series: Series[];
}

function label(v: CellValue): string {
  if (v === null) return "";
  return typeof v === "number" ? String(Number(v.toPrecision(12))) : String(v);
}

/**
 * The range's values (rows of cells) as labels and series. A first row of
 * text names the series; a first column of text labels the categories.
 */
export function chartData(grid: CellValue[][], by: "cols" | "rows" = "cols"): ChartData {
  let rows = grid.map((r) => [...r]);
  if (by === "rows") {
    const width = Math.max(0, ...rows.map((r) => r.length));
    rows = Array.from({ length: width }, (_, c) => rows.map((r) => r[c] ?? null));
  }
  if (rows.length === 0) return { labels: [], series: [] };
  const width = Math.max(...rows.map((r) => r.length));
  const isText = (v: CellValue | undefined): boolean => typeof v === "string" && v !== "";
  const headed = rows[0]?.some((v, i) => i > 0 && isText(v)) ?? false;
  const body = headed ? rows.slice(1) : rows;
  const labelled = width > 1 && body.some((r) => isText(r[0]) || r[0] === null) && body.every((r) => typeof r[0] !== "number");
  const first = labelled ? 1 : 0;
  const series: Series[] = [];
  for (let c = first; c < width; c++) {
    series.push({
      name: headed ? label(rows[0]?.[c] ?? null) : `계열${c - first + 1}`,
      values: body.map((r) => {
        const v = r[c];
        return typeof v === "number" && Number.isFinite(v) ? v : null;
      }),
    });
  }
  const labels = body.map((r, i) => (labelled ? label(r[0] ?? null) : String(i + 1)));
  return { labels, series };
}

/** Round axis steps: 1, 2, 5 x 10^n */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) {
    if (min === 0) return [0, 1];
    min = Math.min(0, min);
    max = Math.max(0, max);
  }
  const raw = (max - min) / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Number(v.toPrecision(12)));
  return ticks;
}

export const PALETTE = ["#4472c4", "#ed7d31", "#a5a5a5", "#ffc000", "#5b9bd5", "#70ad47", "#264478", "#9e480e"];

export interface Shape {
  kind: "rect" | "path" | "line" | "text" | "circle";
  attrs: Record<string, string | number>;
  text?: string;
}

const PAD = { left: 48, right: 12, top: 28, bottom: 36 };

/** The shapes of a chart w x h */
export function chartShapes(type: ChartType, data: ChartData, w: number, h: number, title?: string): Shape[] {
  const out: Shape[] = [];
  if (title) out.push({ kind: "text", attrs: { x: w / 2, y: 18, "text-anchor": "middle", class: "chart-title" }, text: title });
  if (data.series.length === 0 || data.labels.length === 0) {
    out.push({ kind: "text", attrs: { x: w / 2, y: h / 2, "text-anchor": "middle", class: "chart-empty" }, text: "숫자가 없습니다" });
    return out;
  }
  if (type === "pie") return out.concat(pieShapes(data, w, h, title ? 28 : 8));

  const values = data.series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const ticks = niceTicks(Math.min(0, ...values), Math.max(0, ...values));
  const lo = ticks[0] ?? 0;
  const hi = ticks[ticks.length - 1] ?? 1;
  const plotW = Math.max(10, w - PAD.left - PAD.right);
  const plotH = Math.max(10, h - PAD.top - PAD.bottom);
  const y = (v: number): number => PAD.top + plotH - ((v - lo) / (hi - lo || 1)) * plotH;
  for (const t of ticks) {
    out.push({ kind: "line", attrs: { x1: PAD.left, x2: PAD.left + plotW, y1: y(t), y2: y(t), class: "chart-grid" } });
    out.push({
      kind: "text",
      attrs: { x: PAD.left - 6, y: y(t) + 4, "text-anchor": "end", class: "chart-tick" },
      text: t.toLocaleString("ko-KR"),
    });
  }
  const n = data.labels.length;
  const band = plotW / n;
  const every = Math.max(1, Math.ceil((n * 40) / plotW)); // labels need ~40 px
  data.labels.forEach((l, i) => {
    if (i % every !== 0) return;
    out.push({
      kind: "text",
      attrs: { x: PAD.left + band * (i + 0.5), y: PAD.top + plotH + 16, "text-anchor": "middle", class: "chart-tick" },
      text: l.length > 8 ? l.slice(0, 7) + "…" : l,
    });
  });
  if (type === "bar") {
    const k = data.series.length;
    const barW = Math.max(1, (band * 0.8) / k);
    data.series.forEach((s, si) =>
      s.values.forEach((v, i) => {
        if (v === null) return;
        const x = PAD.left + band * i + band * 0.1 + barW * si;
        const top = Math.min(y(v), y(0));
        out.push({
          kind: "rect",
          attrs: { x, y: top, width: barW, height: Math.max(0.5, Math.abs(y(v) - y(0))), fill: PALETTE[si % PALETTE.length] ?? "#888" },
        });
      }),
    );
  } else {
    data.series.forEach((s, si) => {
      let d = "";
      let pen = false;
      s.values.forEach((v, i) => {
        if (v === null) {
          pen = false;
          return;
        }
        const px = PAD.left + band * (i + 0.5);
        d += `${pen ? "L" : "M"}${px.toFixed(1)},${y(v).toFixed(1)}`;
        pen = true;
        out.push({ kind: "circle", attrs: { cx: px, cy: y(v), r: 2.5, fill: PALETTE[si % PALETTE.length] ?? "#888" } });
      });
      out.push({ kind: "path", attrs: { d, fill: "none", stroke: PALETTE[si % PALETTE.length] ?? "#888", "stroke-width": 2 } });
    });
  }
  out.push({ kind: "line", attrs: { x1: PAD.left, x2: PAD.left + plotW, y1: y(0), y2: y(0), class: "chart-axis" } });
  if (data.series.length > 1) legend(out, data.series.map((s) => s.name), w, h);
  return out;
}

function legend(out: Shape[], names: string[], w: number, h: number): void {
  let x = PAD.left;
  names.forEach((name, i) => {
    if (x > w - 40) return;
    out.push({ kind: "rect", attrs: { x, y: h - 12, width: 10, height: 10, fill: PALETTE[i % PALETTE.length] ?? "#888" } });
    out.push({ kind: "text", attrs: { x: x + 14, y: h - 3, class: "chart-legend" }, text: name });
    x += 24 + Math.min(10, name.length) * 11;
  });
}

function pieShapes(data: ChartData, w: number, h: number, top: number): Shape[] {
  const out: Shape[] = [];
  const s = data.series[0];
  if (s === undefined) return out;
  const parts = s.values.map((v, i) => ({ v: v !== null && v > 0 ? v : 0, label: data.labels[i] ?? "" }));
  const total = parts.reduce((a, p) => a + p.v, 0);
  if (total <= 0) {
    out.push({ kind: "text", attrs: { x: w / 2, y: h / 2, "text-anchor": "middle", class: "chart-empty" }, text: "양수가 없습니다" });
    return out;
  }
  const legendW = Math.min(140, w * 0.4);
  const r = Math.max(10, Math.min((w - legendW) / 2, (h - top) / 2) - 8);
  const cx = (w - legendW) / 2;
  const cy = top + (h - top) / 2;
  let a = -Math.PI / 2;
  parts.forEach((p, i) => {
    if (p.v === 0) return;
    const fill = PALETTE[i % PALETTE.length] ?? "#888";
    const sweep = (p.v / total) * Math.PI * 2;
    if (sweep >= Math.PI * 2 - 1e-9) {
      out.push({ kind: "circle", attrs: { cx, cy, r, fill } });
    } else {
      const x1 = cx + r * Math.cos(a);
      const y1 = cy + r * Math.sin(a);
      const x2 = cx + r * Math.cos(a + sweep);
      const y2 = cy + r * Math.sin(a + sweep);
      const large = sweep > Math.PI ? 1 : 0;
      out.push({
        kind: "path",
        attrs: { d: `M${cx},${cy}L${x1.toFixed(2)},${y1.toFixed(2)}A${r},${r} 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)}Z`, fill },
      });
    }
    a += sweep;
  });
  parts.forEach((p, i) => {
    const ly = top + 8 + i * 18;
    if (ly > h - 8) return;
    out.push({ kind: "rect", attrs: { x: w - legendW + 4, y: ly, width: 10, height: 10, fill: PALETTE[i % PALETTE.length] ?? "#888" } });
    const pct = Math.round((p.v / total) * 1000) / 10;
    out.push({ kind: "text", attrs: { x: w - legendW + 18, y: ly + 9, class: "chart-legend" }, text: `${p.label} ${pct}%` });
  });
  return out;
}
